<template>
  <!-- The single sidebar of the design-system look (DesignSystemFeature), modelled on the Demand
       Engine (airlst2027 www/admin/_brand.php): it replaces the top header AND the event sidebar.
       Collapsed it is the Demand Engine's icon rail; expanded it adds labels and the event name.
       Rendered only while the design-system look is on, so it uses the --ds-* tokens directly. -->
  <!-- Below lg the menu button opens the sidebar as a drawer over the page: the same entries with
       their labels. A rail-wide spacer keeps the page where it was underneath it. -->
  <div
    v-if="drawerOpen"
    class="w-(--ds-size-rail-width-compact) shrink-0 lg:hidden"
    aria-hidden="true"
  />
  <div
    v-if="drawerOpen"
    class="fixed inset-0 z-40 bg-(--ds-surface-scrim) lg:hidden"
    data-test="app-sidebar-scrim"
    @click="drawerOpen = false"
  />
  <aside
    class="flex shrink-0 flex-col border-r border-(--ds-border-app) bg-(--ds-surface-default) transition-[width] duration-200"
    :class="[
      collapsed
        ? 'w-(--ds-size-rail-width-compact) lg:w-(--ds-size-rail-width)'
        : 'w-64',
      drawerOpen
        ? 'fixed top-0 left-0 z-50 h-dvh shadow-(--ds-elevation-overlay)'
        : 'sticky top-(--header-height) z-32 h-body',
    ]"
    data-test="app-sidebar"
  >
    <div
      class="mx-[11px] mb-1.5 flex items-center border-b border-(--ds-border-subtle) py-[17px]"
      :class="collapsed ? 'justify-center' : 'px-2'"
    >
      <Link :href="useRoute('dashboard')" aria-label="AirLST">
        <img
          v-if="collapsed"
          :src="Vapor.asset('images/airlst-symbol.svg')"
          alt=""
          class="h-[33px] w-[34px]"
          data-test="logo-img"
        />
        <img
          v-else
          :src="Vapor.asset('images/airlst-logo.svg')"
          alt=""
          class="h-[38px] w-auto"
          data-test="logo-img"
        />
      </Link>
    </div>

    <!-- While the section's own column (SectionNav) is folded away, its way back sits on the spot of
         the sidebar toggle: the section menu comes first, the sidebar toggle returns once it is out. -->
    <button
      v-if="sectionNavFolded"
      type="button"
      class="absolute top-3 -right-3.5 z-1 hidden size-7 items-center justify-center rounded-full border border-(--ds-border-app) bg-(--ds-surface-default) text-(--ds-text-secondary) transition-colors duration-150 hover:border-(--ds-border-brand) hover:text-(--ds-text-brand) lg:flex"
      :aria-label="$t('common.expand_menu')"
      :title="$t('common.expand_menu')"
      data-test="section-nav-expand"
      @click="setSectionNavCollapsed(false)"
    >
      <!-- eslint-disable vue/no-v-html -- the static design-system chevron -->
      <span
        class="flex size-3.5 [&>svg]:size-full"
        aria-hidden="true"
        v-html="ICONS['chevron-right']"
      />
      <!-- eslint-enable vue/no-v-html -->
    </button>

    <!-- Collapse toggle: sticks to the sidebar's right border at its very top, in both states. -->
    <button
      v-else
      type="button"
      class="absolute top-3 -right-3.5 z-1 hidden size-7 items-center justify-center rounded-full border border-(--ds-border-app) bg-(--ds-surface-default) text-(--ds-text-secondary) transition-colors duration-150 hover:border-(--ds-border-brand) hover:text-(--ds-text-brand) lg:flex"
      :aria-label="
        $t(
          collapsed
            ? 'sidebar.toggle.open.tooltip'
            : 'sidebar.toggle.close.tooltip',
        )
      "
      :title="
        $t(
          collapsed
            ? 'sidebar.toggle.open.tooltip'
            : 'sidebar.toggle.close.tooltip',
        )
      "
      data-test="app-sidebar-toggle"
      @click="emit('toggle-sidebar', !collapsed)"
    >
      <FontAwesomeIcon :icon="['far', 'sidebar']" class="text-sm" />
    </button>

    <!-- Below lg: the menu button that opens the drawer (and closes it again). The bell and the
         user menu are top right in the headline row at every width (HeaderActions). -->
    <div
      class="flex items-center py-2 lg:hidden"
      :class="drawerOpen ? 'px-[9px]' : 'justify-center'"
      data-test="app-sidebar-rail-top"
    >
      <button
        type="button"
        class="flex size-[42px] items-center justify-center rounded-(--ds-radius-md) text-(--ds-text-secondary) transition-colors duration-150 hover:bg-(--ds-color-grey-50) hover:text-(--ds-text-primary)"
        :aria-label="$t(drawerOpen ? 'common.close' : 'common.menu')"
        :aria-expanded="drawerOpen ? 'true' : 'false'"
        data-test="app-sidebar-menu-button"
        @click="drawerOpen = !drawerOpen"
      >
        <!-- eslint-disable vue/no-v-html -- the static design-system icon -->
        <span
          class="flex size-[22px] [&>svg]:size-full"
          aria-hidden="true"
          v-html="ICONS[drawerOpen ? 'close' : 'menu']"
        />
        <!-- eslint-enable vue/no-v-html -->
      </button>
    </div>

    <nav
      class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-[11px] py-2"
      :aria-label="$t('common.app_navigation')"
    >
      <!-- Where you are (SN-069): the event inside one, the organisation outside. The switcher
           moves between them; without it the place is named only. -->
      <div
        v-if="canOpenSwitcher || !collapsed"
        :class="[
          // The switcher spans the sidebar and pads itself, so its hover reaches the edges.
          collapsed ? 'mx-1' : !canOpenSwitcher && 'px-2',
          !collapsed && 'pt-1',
          'mb-1 border-b border-(--ds-border-subtle) pb-2',
        ]"
        data-test="app-sidebar-context"
      >
        <EventSwitcher
          v-if="canOpenSwitcher"
          :event="event ?? null"
          :collapsed="collapsed"
          :can-list-events="canSwitchEvent"
          :organisation-href="organisationHref"
        />
        <template v-else-if="!collapsed && event">
          <p
            class="break-words text-body font-semibold text-(--ds-text-primary)"
            v-text="event.name"
          />
          <p
            class="mt-0.5 text-caption text-(--ds-text-secondary)"
            v-text="customFormat(event.starts_at, 'MMMM dd, yyyy')"
          />
        </template>
        <template v-else-if="!collapsed">
          <p
            class="text-caption text-(--ds-text-secondary)"
            v-text="$t('common.organisation')"
          />
          <p
            class="mt-0.5 break-words text-body font-semibold text-(--ds-text-primary)"
            data-test="app-sidebar-organisation-name"
            v-text="companyName"
          />
        </template>
      </div>

      <Tooltip
        v-for="item in globalItems"
        :key="item.key"
        :description="item.label"
        :tooltip-class="!collapsed ? 'hidden' : ''"
        width="w-max"
        placement="right"
        teleport
      >
        <Link
          :href="item.href"
          :class="itemClass(item.active)"
          :aria-label="item.label"
          :aria-current="item.active ? 'page' : undefined"
          :data-test="`app-sidebar-${item.key}`"
          @click="item.section && setSectionNavCollapsed(false)"
        >
          <img
            v-if="sidebarIllustration(item.key)"
            :src="Vapor.asset(sidebarIllustration(item.key))"
            alt=""
            class="size-7 flex-none"
          />
          <FontAwesomeIcon v-else :icon="item.icon" class="w-5 text-[19px]" />
          <span v-if="!collapsed" class="truncate" v-text="item.label" />
          <span
            v-if="!collapsed && item.badge"
            class="ml-auto shrink-0 rounded-(--ds-radius-pill) bg-(--ds-surface-brand) px-2 py-0.5 text-[11px] font-semibold text-(--ds-text-brand)"
            :data-test="`app-sidebar-${item.key}-badge`"
            v-text="item.badge"
          />
        </Link>
      </Tooltip>

      <template v-if="event">
        <ul class="flex flex-col gap-1.5">
          <li
            v-for="menu in eventMenu"
            :key="menu.label"
            class="relative flex items-center"
            :class="menu.coming_soon && 'pointer-events-none'"
            @click="setSectionNavCollapsed(false)"
          >
            <!-- Sections with a submenu open their first page; their pages are tabs there
                 (SectionTabs), so there is no flyout. -->
            <Tooltip
              class="w-full justify-start"
              :tooltip-class="!collapsed ? 'hidden' : ''"
              :description="$t(`common.${menu.label}`)"
              width="w-max"
              placement="right"
              teleport
            >
              <!-- The label below only renders while the sidebar is open, so collapsed these are
                   bare icons; the tooltip is a hover affordance and names nothing. aria-label as
                   the global items above already do. -->
              <!-- Below lg a section built for a big screen (menu.js `desktopOnly`, the editors and
                   builders) does not open: it shows dimmed, with a desktop icon in the drawer, and
                   a tap explains why, as the classic phone menu does. -->
              <button
                v-if="isDesktopOnlyHere(menu)"
                type="button"
                :class="itemClass(false)"
                :aria-label="`${$t(`common.${menu.label}`)}, ${$t('sidebar.desktop_only')}`"
                :data-test="`app-sidebar-desktop-only-${menu.label}`"
                @click.stop="showDesktopOnly = true"
              >
                <img
                  v-if="sidebarIllustration(menu.label)"
                  :src="Vapor.asset(sidebarIllustration(menu.label))"
                  alt=""
                  class="size-7 flex-none opacity-40 grayscale"
                />
                <FontAwesomeIcon
                  v-else-if="menu.icon"
                  :icon="menu.icon"
                  class="w-5 text-[19px] opacity-40"
                />
                <template v-if="!collapsed">
                  <span
                    class="truncate text-(--ds-text-muted)"
                    v-text="$t(`common.${menu.label}`)"
                  />
                  <!-- eslint-disable vue/no-v-html -- the static design-system icon -->
                  <span
                    class="ml-auto flex size-4 flex-none text-(--ds-text-muted) [&>svg]:size-full"
                    :title="$t('sidebar.desktop_only')"
                    aria-hidden="true"
                    v-html="ICONS.desktop"
                  />
                  <!-- eslint-enable vue/no-v-html -->
                </template>
              </button>
              <component
                :is="menu.newTab ? 'a' : Link"
                v-else
                :class="itemClass(isMenuActive(menu))"
                :href="menuHref(menu)"
                :aria-label="$t(`common.${menu.label}`)"
                :aria-current="isMenuActive(menu) ? 'page' : undefined"
                :target="menu.newTab ? '_blank' : undefined"
                :rel="menu.newTab ? 'noopener noreferrer' : undefined"
              >
                <img
                  v-if="sidebarIllustration(menu.label)"
                  :src="Vapor.asset(sidebarIllustration(menu.label))"
                  alt=""
                  class="size-7 flex-none"
                  :class="menu.coming_soon && 'opacity-50'"
                />
                <span
                  v-else-if="menu.dsIcon && ICONS[menu.dsIcon]"
                  class="flex size-7 flex-none items-center justify-center text-(--ds-text-brand)"
                  :class="menu.coming_soon && 'opacity-50'"
                >
                  <DsIcon :name="menu.dsIcon" class="size-5!" />
                </span>
                <FontAwesomeIcon
                  v-else-if="menu.icon"
                  :icon="menu.icon"
                  class="w-5 text-[19px]"
                  :class="menu.coming_soon && 'opacity-50'"
                />
                <span
                  v-if="!collapsed"
                  class="truncate"
                  :class="menu.coming_soon && 'opacity-50'"
                  v-text="$t(`common.${menu.label}`)"
                />
              </component>
            </Tooltip>
          </li>
        </ul>
      </template>
    </nav>

    <!-- The bell and the user menu sit top right in the headline row (HeaderActions). Only the
         full-screen tools without one (the App Builder, the Airstage canvas) keep them down here:
         floating top right, they drew over the tool's own panel header. -->
    <div
      v-if="hasChecklist || accountInFoot"
      class="items-center justify-center gap-2 border-t border-(--ds-border-subtle) px-2.5 pt-3 pb-[15px]"
      :class="[
        collapsed ? 'flex-col' : 'flex-row',
        accountInFoot ? 'flex' : 'hidden lg:flex',
      ]"
      data-test="app-sidebar-foot"
    >
      <ChecklistDropdown
        v-if="hasChecklist"
        class="max-lg:hidden"
        dropdown-class="left-full! right-auto! bottom-0 ml-3 origin-bottom-left"
        :checked="event.checklist.checked_items"
        :items="useEventChecklistItems()"
      />

      <template v-if="accountInFoot">
        <Notifications
          menu-class="flex"
          menu-btn-class="flex! size-[42px]! items-center justify-center rounded-full! bg-(--ds-action-brand) py-0! text-(--ds-color-white) transition-colors duration-150 hover:bg-(--ds-color-violet-d300)"
          dropdown-class="left-full! right-auto! bottom-0 ml-3 origin-bottom-left max-lg:w-[calc(100vw-84px)]! max-lg:max-w-96"
        />

        <UserDropdown />
      </template>
    </div>
  </aside>

  <DisabledMenuAlert
    :show="showDesktopOnly"
    @confirm="showDesktopOnly = false"
  />
</template>

<script setup>
import DsIcon from '@/Components/common/DsIcon.vue'
import Tooltip from '@/Components/common/Tooltip.vue'
import ChecklistDropdown from '@/Components/ChecklistDropdown.vue'
import DisabledMenuAlert from '@/Components/DisabledMenuAlert.vue'
import EventSwitcher from '@/Components/Sidebar/EventSwitcher.vue'
import Notifications from '@/Components/Notifications/Notifications.vue'
import UserDropdown from '@/Components/UserSettings/UserDropdown.vue'
import { useEventChecklistItems } from '@/composables/constants'
import { customFormat } from '@/composables/date'
import { sidebarIllustration } from '@/Libraries/sidebarIllustrations'
import {
  isMenuEntryHidden,
  useVisibleMenuElements,
} from '@/composables/navigationPreferences'
import { ICONS } from '@/Libraries/design-system-icons'
import { useIsBelowDesktop } from '@/composables/mediaQuery'
import {
  foldablePageColumn,
  sectionNavCollapsed,
  setSectionNavCollapsed,
  useCurrentSectionMenu,
  useSectionHostPage,
  useSectionMenus,
} from '@/composables/sectionTabs'
import { usePermission } from '@/composables/permission'
import { useFeature } from '@/composables/feature'
import { useRoute } from '@/composables/route'
import { orgContentHref } from '@/composables/orgContent'
import { hasCrmAccess } from '@/composables/subscription'
import {
  isDamTrialRunning,
  isPlannerTrialRunning,
} from '@/composables/trialNavigation'
import { Link, router, usePage } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps({
  /** The page has no headline row (App Builder, Airstage canvas): the bell and the user menu are here. */
  accountInFoot: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['toggle-sidebar'])

const { t } = useI18n()

const isSidebarCollapsed = inject('isSidebarCollapsed')

/**
 * Below `lg` the sidebar is the icon rail while closed, then an expanded drawer while open,
 * regardless of the stored desktop preference.
 *
 * The stored preference defaults to expanded (Default.vue, `ref(false)`), and the collapse toggle
 * is `lg:flex` - so on a phone the expanded 256px sidebar covered two thirds of the viewport with
 * no control anywhere to undo it: 115px of content at 375px, 60px at 320px. At `lg` and above this
 * is exactly `isSidebarCollapsed` again, so the desktop sidebar is untouched.
 */
const isBelowDesktop = useIsBelowDesktop()

/** Below lg: the sidebar is open as a drawer over the page (the menu button in the rail). */
const drawerOpen = ref(false)

const collapsed = computed(() =>
  isBelowDesktop.value ? !drawerOpen.value : isSidebarCollapsed.value === true,
)

/** A section built for a big screen, on a screen below lg (menu.js `desktopOnly`). */
const isDesktopOnlyHere = (menu) =>
  isBelowDesktop.value && menu.desktopOnly === true

const showDesktopOnly = ref(false)

const closeDrawer = () => {
  drawerOpen.value = false
}

// The drawer is a way to a page: it closes once one is opened, on Escape, and when the screen
// grows past lg, where the sidebar is no drawer.
watch(isBelowDesktop, (below) => !below && closeDrawer())

const onKeydown = (event) => event.key === 'Escape' && closeDrawer()
let stopNavigateListener = null

onMounted(() => {
  stopNavigateListener = router.on('navigate', closeDrawer)
  window.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  stopNavigateListener?.()
  window.removeEventListener('keydown', onKeydown)
})

/**
 * The current section's column is folded away (Default.vue shows it on every section page: an
 * event's, or outside an event the organisation's, such as the asset library), or a page column
 * folding with it (the Airstage builder's pages): its expand arrow then takes the sidebar
 * toggle's place.
 */
const sectionNavFolded = computed(
  () =>
    sectionNavCollapsed.value &&
    (useCurrentSectionMenu() !== null || foldablePageColumn.value),
)

const event = computed(() => usePage().props.event)
const hasChecklist = computed(
  () => event.value?.checklist?.has_checklist === true,
)
const eventMenu = computed(() => (event.value ? useVisibleMenuElements() : []))

const isCompanyActive = () =>
  usePage().props._user.active_company?.deactivated_at === null

/**
 * The event switcher, under the same rule as the classic header's "My events" dropdown it replaces:
 * with event:list, for an active company. Unlike the classic look it is also in the phone rail and
 * drawer.
 */
const canSwitchEvent = computed(
  () => usePermission('event:list') && isCompanyActive(),
)

const companyName = computed(
  () => usePage().props._user.active_company?.name ?? '',
)

/**
 * The organisation level's entries (SN-069): Events, Kontakte and Inhalte (Medien, Texte in the
 * section column). Each keeps the gate it had as a separate entry; Inhalte shows while one of its
 * pages does.
 */
const organisationItems = computed(() => {
  const page = usePage()

  // Organisation & Event Sidebar (Ingmar Heinrich, 20 Oct 2026): the company entries belong to the
  // organisation level only; inside an event the sidebar shows only the event, and the switcher's
  // Organisation row leads back. Order: Events, Planung, CRM, Inhalte › Medien · Texte.
  const hasMedia =
    (useFeature('marketplace') || useFeature('dam')) &&
    usePermission('dam:view') &&
    !isMenuEntryHidden('dam')
  const hasTexts =
    (useFeature('marketplace') || useFeature('content')) &&
    usePermission('content:text:list')

  return [
    usePermission('event:list') &&
      isCompanyActive() && {
        key: 'events',
        label: t('common.events'),
        icon: ['fas', 'party-horn'],
        href: useRoute('dashboard'),
        active: page.component === 'Dashboard',
      },
    // Planung at organisation level (package 15): its own entry, not part of Meine Events —
    // while hidden it leads to the notice page (package 28, T4c).
    !event.value &&
      isCompanyActive() &&
      !isMenuEntryHidden('planner_org') &&
      (page.props._planner?.org?.canView === true ||
        page.props._planner?.org?.hidden === true ||
        page.props._planner?.org?.canStart === true) && {
        key: 'planner',
        label: t('planner.org.title'),
        badge: isPlannerTrialRunning(page)
          ? t('marketplace.state.trial')
          : null,
        icon: ['far', 'clipboard-list'],
        // Package 29 (B3): `from=menu` shows the page „Testphase beendet“ after the trial.
        href: useRoute('planner.org.tasks', { from: 'menu' }),
        // Package 23: the planner settings belong to Planung as well.
        active:
          page.component.startsWith('Planner/Org/') ||
          page.component.startsWith('Planner/Settings/') ||
          // QA-22: once hidden the organisation's Planung shows the onboarding screen.
          (['Planner/Ended', 'Planner/Start'].includes(page.component) &&
            !page.props.event),
      },
    // „Meine Kontakte" is now „CRM" with the badge „Basis" (Arnold, 14 October 2026; kept on 22 Oct).
    usePermission('contact:list') &&
      hasCrmAccess() &&
      !isMenuEntryHidden('crm') && {
        key: 'contacts',
        label: t('contact.my_contacts'),
        badge: t('sidebar.badge.basis'),
        icon: ['fas', 'address-book'],
        href: useRoute('contacts.index'),
        active: page.component.includes('Contacts'),
      },
    // Inhalte is the Zentrale Inhalte package, the asset library (Medien) and the texts (Texte) in one
    // section; its pages sit in the section column (OrgContentNav). The entry opens the start page from
    // the navigation (onboarding, library or, in the read window, the offboarding; 22 Oct 2026); the
    // badge „Trial" only while the package trial runs (_damTrial, Arnold 12 and 14 October 2026, R8).
    (hasMedia || hasTexts) && {
      key: 'content',
      label: t('common.content'),
      badge: isDamTrialRunning(page) ? t('marketplace.state.trial') : null,
      icon: ['fas', 'layer-group'],
      href: orgContentHref(),
      section: true,
      active:
        page.component.startsWith('Dam/') ||
        page.component.startsWith('Content/'),
    },
  ].filter(Boolean)
})

/** Inside an event the organisation's entries make way for the event's; the switcher leads back. */
const globalItems = computed(() => (event.value ? [] : organisationItems.value))

/** The switcher's Organisation row: the first organisation entry the user may open. */
const organisationHref = computed(
  () => organisationItems.value[0]?.href ?? null,
)

/**
 * The switcher shows for event:list (its event list) and for anyone who can reach the
 * organisation level from it (a library user without event:list gets only its Organisation row).
 */
const canOpenSwitcher = computed(
  () => canSwitchEvent.value || organisationHref.value !== null,
)

const itemClass = (active) => [
  'flex w-full items-center gap-3 rounded-(--ds-radius-md) text-ui font-medium transition-colors duration-150',
  collapsed.value ? 'h-[46px] justify-center' : 'h-10 px-3',
  active
    ? 'bg-(--ds-surface-brand) text-(--ds-text-brand)'
    : 'text-(--ds-text-secondary) hover:bg-(--ds-color-grey-50) hover:text-(--ds-text-primary)',
]

/**
 * A section with a submenu has no flyout here: it opens its first visible page, and its pages
 * switch through the tab bar on top of them (SectionTabs).
 */
const sectionChildren = (menu) =>
  useSectionMenus().find((section) => section.name === menu.name)?.child ?? []

const menuHref = (menu) => {
  const target = menu.dropdown ? (sectionChildren(menu)[0] ?? menu) : menu
  return useRoute(target.route, {
    event: event.value.id,
    ...(target.extraParams ?? {}),
    ...(target.entryParams ?? {}),
  })
}

/**
 * Same rule as the event sidebar: exact component match, with a page that belongs to an entry
 * without being one (a create form, an archive, the Airstage builders) resolved to that entry
 * first - the same map the section column uses, so the rail and the column never disagree.
 */
const isMenuActive = (menu) => {
  const component = useSectionHostPage(usePage().component)
  if (menu?.dropdown) {
    return sectionChildren(menu).some((child) => child.name === component)
  }
  return component === menu?.name
}
</script>
